import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {AppDispatch, RootState} from 'store/store';
import {UserJobRoleThunks} from './userJobRoleThunks';
import {DocumentTemplateThunks} from 'features/documentTemplate/documentTemplateThunks';
import CustomSelect from 'components/inputs/CustomSelect';
import SubmitButton from 'components/SubmitButton';
import {UserJobRole} from 'types/userJobRole';
import {DocumentTemplate} from 'types/documentTemplate';
import XIcon from 'assets/images/icons/XIcon';
import DynamicTextBlockInputs from '../../components/documentTemplate/DynamicTextBlockInputs';
import DynamicFillOutOnCreationInputs from '../../components/documentTemplate/DynamicFillOutOnCreationInputs';
import {TextBlockElementClass, DocumentTemplateElement} from '../../types/documentTemplateElement';
import buildDocumentTemplateSnapshot from '../../utils/buildDocumentTemplateSnapshot';
import {JobRole} from '../../types/jobRole';
import {JobRoleThunks} from '../jobRole/jobRoleThunks';

interface UserJobRoleFormProps {
  closeDrawer: () => void;
  isDrawerOpen: boolean;
  userId: string;
}

const UserJobRoleForm: React.FC<UserJobRoleFormProps> = ({
  closeDrawer,
  userId,
  isDrawerOpen,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const userJobRole = useSelector(
    (state: RootState) => state.userJobRole.userJobRole,
  );
  const documentTemplates = useSelector(
    (state: RootState) => state.documentTemplate.documentTemplates,
  );
  const [updatedUserJobRole, setUpdatedUserJobRole] = useState<
    Partial<UserJobRole>
  >({
    ...userJobRole,
    userId,
    status: 'missing',
  });
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [textBlockElements, setTextBlockElements] = useState<
    TextBlockElementClass[]
  >([]);
  const [fillOutOnCreationElements, setFillOutOnCreationElements] = useState<
    DocumentTemplateElement[]
  >([]);
  const [elementValues, setElementValues] = useState<Record<string, string>>(
    {},
  );
  const [fillOutOnCreationValues, setFillOutOnCreationValues] = useState<Record<string, any>>(
    {},
  );
  const [areAllInputsFilled, setAreAllInputsFilled] = useState(false);
  const [areAllFillOutOnCreationInputsFilled, setAreAllFillOutOnCreationInputsFilled] = useState(false);
  const user = useSelector((state: RootState) => state.user.user);
  const jobRoles = useSelector((state: RootState) => state.jobRole.jobRoles);

  const jobRolesOptions = jobRoles.map((jobRole: JobRole) => ({
    value: jobRole.id,
    label: jobRole.name,
  }));
  const [selectedJobRole, setSelectedJobRole] = useState<JobRole | undefined>(
    undefined,
  );

  useEffect(() => {
    const loadDocumentTemplates = async () => {
      try {
        await dispatch(
          DocumentTemplateThunks.index({
            page: 1,
            search: {where: {}},
            sortField: '',
            sortDirection: '',
            rowsPerPage: 100,
          }),
        );
      } catch (err) {
        console.error(err);
      }
    };
    const loadJobRoles = async () => {
      try {
        await dispatch(
          JobRoleThunks.index({
            page: 1,
            search: {where: {}},
            sortField: '',
            sortDirection: '',
            rowsPerPage: 100,
          }),
        );
      } catch (err) {
        console.error(err);
      }
    };
    loadJobRoles();
    loadDocumentTemplates();
  }, [dispatch]);

  useEffect(() => {
    const isFormValid = updatedUserJobRole.jobRoleId !== undefined;
    const shouldCheckTextBlocks = textBlockElements.length > 0;
    const shouldCheckFillOutOnCreation = fillOutOnCreationElements.length > 0;

    setIsSaveDisabled(
      !isFormValid || 
      (shouldCheckTextBlocks && !areAllInputsFilled) ||
      (shouldCheckFillOutOnCreation && !areAllFillOutOnCreationInputsFilled),
    );
  }, [updatedUserJobRole.jobRoleId, textBlockElements, areAllInputsFilled, fillOutOnCreationElements, areAllFillOutOnCreationInputsFilled]);

  useEffect(() => {
    setUpdatedUserJobRole({
      ...userJobRole,
      userId,
      status: userJobRole?.status || 'missing',
    });
  }, [userJobRole, userId]);

  useEffect(() => {
    if (updatedUserJobRole.documentTemplateId) {
      const selectedTemplate = documentTemplates.find(
        (template: DocumentTemplate) =>
          template.id === updatedUserJobRole.documentTemplateId,
      );

      if (selectedTemplate) {
        const textBlockElements: TextBlockElementClass[] = [];
        const fillOutOnCreationElements: DocumentTemplateElement[] = [];
        
        selectedTemplate.rows?.forEach((row: any) => {
          row.columns.forEach((column: any) => {
            if (column.element) {
              if (column.element.elementType === 'TextBlock') {
                textBlockElements.push(column.element as TextBlockElementClass);
              } else if (column.element.properties?.fillOutOnCreation) {
                fillOutOnCreationElements.push(column.element as DocumentTemplateElement);
              }
            }
          });
        });
        
        setTextBlockElements(textBlockElements);
        setFillOutOnCreationElements(fillOutOnCreationElements);

        setElementValues({});
        setFillOutOnCreationValues({});
        setAreAllInputsFilled(textBlockElements.length === 0);
        setAreAllFillOutOnCreationInputsFilled(fillOutOnCreationElements.length === 0);
      }
    }
  }, [updatedUserJobRole.documentTemplateId, documentTemplates]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    let solutionAttributes = null;

    if (updatedUserJobRole.documentTemplateId) {
      const documentTemplateSnapshot = buildDocumentTemplateSnapshot(
        documentTemplates,
        updatedUserJobRole.documentTemplateId,
        elementValues,
        fillOutOnCreationValues,
      );

      if (!documentTemplateSnapshot) {
        console.error('Template not found');
        return;
      }

      solutionAttributes = {
        documentTemplateId: updatedUserJobRole.documentTemplateId,
        snapshot: documentTemplateSnapshot,
      };
    }

    const payload = {
      ...updatedUserJobRole,
      userId: userId,
      status: 'missing',
      ...(solutionAttributes && {
        documentTemplateSolutionAttributes: solutionAttributes,
      }),
    };

    if (userJobRole?.id) {
      await dispatch(UserJobRoleThunks.update(payload as UserJobRole)).then(
        () => closeDrawer(),
      );
    } else {
      const newUserJobRole = new UserJobRole(payload);
      await dispatch(
        UserJobRoleThunks.create(newUserJobRole as UserJobRole),
      ).then(() => closeDrawer());
    }
  };

  useEffect(() => {
    if (!isDrawerOpen) handleClose();
  }, [isDrawerOpen]);

  const handleElementUpdate = (elementId: string, content: string) => {
    setElementValues(prev => ({
      ...prev,
      [elementId]: content,
    }));
  };

  const handleFillOutOnCreationUpdate = (elementId: string, value: any) => {
    setFillOutOnCreationValues(prev => ({
      ...prev,
      [elementId]: value,
    }));
  };

  const handleAllInputsFilled = (allFilled: boolean) => {
    setAreAllInputsFilled(allFilled);
  };

  const handleAllFillOutOnCreationInputsFilled = (allFilled: boolean) => {
    setAreAllFillOutOnCreationInputsFilled(allFilled);
  };

  const handleClose = () => {
    setUpdatedUserJobRole({
      name: '',
      jobRoleId: '',
      documentTemplateId: '',
    });
    setTextBlockElements([]);
    setFillOutOnCreationElements([]);
    setElementValues({});
    setFillOutOnCreationValues({});
    setAreAllInputsFilled(false);
    setAreAllFillOutOnCreationInputsFilled(false);
  };

  return (
    <div className="flex flex-col h-screen">
      <div className="flex-1 flex flex-col">
        <div className="h-auto p-6 bg-sky-50 border-b border-sky-200 flex justify-between items-start gap-2 w-full">
          <div className="flex flex-col justify-start items-start gap-1">
            <div className="text-lg font-semibold tracking-normal text-cyan-800">
              {userJobRole?.id ? 'Edit Role' : 'Create new Role'}
            </div>
            <div className="text-sm font-light tracking-normal leading-5 text-zinc-400">
              {userJobRole?.id ? 'Edit' : 'Create'} an Role for a document
            </div>
          </div>
          <button
            onClick={closeDrawer}
            className="text-gray-500 hover:text-gray-700 focus:outline-none pt-2">
            <XIcon />
          </button>
        </div>
        <div className="h-full px-6 pb-6">
          <div className="mt-5">
            <form onSubmit={handleSubmit}>
              <div className="flex flex-col space-y-6 items-center mt-7">
                <div className="w-full">
                  <div className="flex flex-col gap-[0.625rem]">
                    <label
                      htmlFor="roleId"
                      className="w-full block grow shrink basis-0 text-slate-800 text-sm font-semibold font-['Inter'] dark:text-white ">
                      Role
                    </label>
                    <CustomSelect
                      options={jobRolesOptions}
                      onChange={selectedOption => {
                        setUpdatedUserJobRole({
                          ...updatedUserJobRole,
                          jobRoleId: selectedOption.value,
                        });
                        setSelectedJobRole(
                          jobRoles.find(
                            (jobRole: JobRole) =>
                              jobRole.id === selectedOption.value,
                          ) || undefined,
                        );
                      }}
                      value={updatedUserJobRole.jobRoleId}
                    />
                    <label
                      htmlFor="documentTemplateId"
                      className="w-full block grow shrink basis-0 text-slate-800 text-sm font-semibold font-['Inter'] dark:text-white ">
                      Document
                    </label>
                    <CustomSelect
                      options={documentTemplates.map(
                        (template: DocumentTemplate) => ({
                          label: template.name,
                          value: template.id,
                        }),
                      )}
                      onChange={selectedOption => {
                        setUpdatedUserJobRole({
                          ...updatedUserJobRole,
                          documentTemplateId: selectedOption.value,
                        });
                      }}
                      value={updatedUserJobRole?.documentTemplateId}
                    />

                    {textBlockElements.length > 0 && user && (
                      <div className="mt-6">
                        <label className="block text-slate-800 text-sm font-semibold font-['Inter'] dark:text-white mb-4 mt-4">
                          Please fill out the following fields
                        </label>
                        <DynamicTextBlockInputs
                          elements={textBlockElements}
                          onUpdateElement={handleElementUpdate}
                          user={user}
                          filterSupervisorsByRole={true}
                          selectedJobRole={selectedJobRole}
                          onUpdateAllInputsFilled={handleAllInputsFilled}
                        />
                      </div>
                    )}
                    {fillOutOnCreationElements.length > 0 && user && (
                      <div className="mt-6">
                        <label className="block text-slate-800 text-sm font-semibold font-['Inter'] dark:text-white mb-4 mt-4">
                          Please fill out the following fields
                        </label>
                        <DynamicFillOutOnCreationInputs
                          elements={fillOutOnCreationElements}
                          onUpdateElement={handleFillOutOnCreationUpdate}
                          user={user}
                          filterSupervisorsByRole={true}
                          selectedJobRole={selectedJobRole}
                          onUpdateAllInputsFilled={handleAllFillOutOnCreationInputsFilled}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
      <footer className="flex-shrink-0 px-6 pb-4 pt-2 flex justify-end gap-5">
        <div className="border-t border-slate-200 w-full">
          <div className="flex w-full justify-center items-center gap-x-2 pb-4">
            <SubmitButton
              onClick={handleSubmit}
              label="Submit"
              disabled={isSaveDisabled}
            />
          </div>
          <div className="flex w-full justify-center items-center gap-x-2 pb-4">
            <button
              type="submit"
              onClick={closeDrawer}
              className="self-center text-sm font-light tracking-normal leading-5 text-center text-zinc-400 hover:text-cyan-400">
              Never mind
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default UserJobRoleForm;
